/**
 * The `Facts` catalog component: one subject's figures, named and stacked.
 *
 * A quote, a position — the single-subject case. It is what `Readings` draws
 * without a state to colour, and what `Grid` refuses because one column is a
 * fact sheet rather than a comparison.
 *
 * NO STATE AND NO TONE. A price is not fine, watch or worse, and it is not
 * good news or bad — deciding either would be the engine judging a figure it
 * was asked to report. The design colours movement and leaves a level alone,
 * so everything here is ink and the label carries the meaning.
 *
 * The generic case is a compact label-value ledger. NEPSE has a known
 * hierarchy: its level and move lead, then activity and trading ranges;
 * additional facts remain visible below instead of becoming more tiles.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import {
  captionLabel,
  humanLabel,
  cellDirection,
  cellNumber,
  cellUnit,
} from '@/features/mitra/lib/figures'
import { Fig, Label } from '@/features/mitra/panel/blocks/Card'
import { useLabelLang } from '@/features/mitra/panel/blocks/charts/labelLang'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'
import { copy, type Lang } from '@/features/mitra/lib/copy'

export const FactsApi = {
  name: 'Facts',
  schema: z
    .object({
      facts: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/facts`, fixed by `to_messages`. */
export type FactPart = { id: string; label: string; value: CellValue }

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const factsApi = FactsApi as unknown as ComponentApi

export const Facts = createComponentImplementation<ComponentApi>(
  factsApi,
  ({ props }: { props: { facts?: unknown; caption?: unknown } }) => (
    <FactSheet
      facts={listOf<FactPart>(props.facts)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

function key(fact: FactPart): string {
  return fact.id.toLowerCase().replace(/[^a-z0-9]+/g, '_')
}

function find(facts: FactPart[], ...names: string[]): FactPart | undefined {
  return facts.find((fact) => names.includes(key(fact)))
}

/**
 * The index card names these rows by their KEY rather than by the label the
 * server sent, because its spaced label ("below 52 week high pct") is a
 * column name. The words live in `lib/figures.ts` with every other figure's,
 * in each register.
 */
const INDEX_KEYS = new Set([
  'previous_close',
  'volume',
  'below_52_week_high',
  'below_52_week_high_pct',
  'above_52_week_low',
  'above_52_week_low_pct',
])

function FactRow({ fact, lang, index = false }: { fact: FactPart; lang: Lang; index?: boolean }) {
  return (
    <div data-role="fact" className="agent-fact-row">
      <dt>
        {index && INDEX_KEYS.has(key(fact))
          ? humanLabel(key(fact), lang)
          : humanLabel(fact.label, lang)}
      </dt>
      <dd>
        <Fig cell={fact.value} quietUnit />
      </dd>
    </div>
  )
}

function Range({
  title,
  low,
  high,
  current,
  lang,
}: {
  title: string
  low: FactPart
  high: FactPart
  current: FactPart
  lang: Lang
}) {
  const words = copy(lang)
  const min = cellNumber(low.value)
  const max = cellNumber(high.value)
  const value = cellNumber(current.value)
  const position =
    Number.isFinite(min) && Number.isFinite(max) && Number.isFinite(value) && max > min
      ? Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))
      : null
  return (
    <div data-role="index-range" className="agent-index-range">
      <p>{title}</p>
      {position !== null && (
        <div className="agent-index-track" aria-hidden="true">
          <span style={{ left: `${String(position)}%` }} />
        </div>
      )}
      <div className="agent-index-range-ends">
        <div>
          <small>{words.factsLow}</small>
          <Fig cell={low.value} quietUnit />
        </div>
        <div>
          <small>{words.factsHigh}</small>
          <Fig cell={high.value} quietUnit />
        </div>
      </div>
    </div>
  )
}

function IndexFacts({ facts, caption, lang }: { facts: FactPart[]; caption: string; lang: Lang }) {
  const words = copy(lang)
  const price = find(facts, 'price', 'level', 'index_value')
  const change = find(facts, 'change_pct', 'change_percent', 'change')
  // Never actually null here: `FactSheet` only renders this component once
  // it has already confirmed both are present. Guarded again rather than
  // asserted, since nothing links that check to this one at the type level.
  if (!price || !change) return null
  const previous = find(facts, 'previous_close', 'prev_close')
  const volume = find(facts, 'volume')
  const dayLow = find(facts, 'day_low', 'low')
  const dayHigh = find(facts, 'day_high', 'high')
  const yearLow = find(facts, 'week_52_low', 'low_52_week', 'fifty_two_week_low')
  const yearHigh = find(facts, 'week_52_high', 'high_52_week', 'fifty_two_week_high')
  const used = new Set(
    [price, change, previous, volume, dayLow, dayHigh, yearLow, yearHigh].filter(Boolean),
  )
  const rest = facts.filter((fact) => !used.has(fact))
  return (
    <figure
      data-role="chart"
      data-component="facts"
      data-variant="index"
      className="agent-index-facts"
    >
      <div className="agent-index-hero">
        <figcaption>
          {captionLabel(caption)} <span>{words.factsIndex}</span>
        </figcaption>
        <div className="agent-index-hero-figures">
          <Fig cell={price.value} quietUnit />
          <span
            data-role="index-change"
            data-direction={cellDirection(change.value)}
            className="agent-index-change"
          >
            <Fig cell={change.value} quietUnit />
          </span>
        </div>
      </div>
      {(previous || volume) && (
        <dl className="agent-index-quick">
          {previous && <FactRow fact={previous} lang={lang} index />}
          {volume && <FactRow fact={volume} lang={lang} index />}
        </dl>
      )}
      {((dayLow && dayHigh) || (yearLow && yearHigh)) && (
        <section className="agent-index-ranges" aria-label={words.factsTradingRanges}>
          {dayLow && dayHigh && (
            <Range
              title={words.factsTodaysRange}
              low={dayLow}
              high={dayHigh}
              current={price}
              lang={lang}
            />
          )}
          {yearLow && yearHigh && (
            <Range
              title={humanLabel('week_52_range', lang)}
              low={yearLow}
              high={yearHigh}
              current={price}
              lang={lang}
            />
          )}
        </section>
      )}
      {rest.length > 0 && (
        <dl className="agent-facts-extra">
          {rest.map((fact) => (
            <FactRow key={fact.id} fact={fact} lang={lang} index />
          ))}
        </dl>
      )}
    </figure>
  )
}

/** The drawing, given the list the surface resolved. */
export function FactSheet({ facts, caption }: { facts: FactPart[]; caption: string | null }) {
  const lang = useLabelLang()
  if (facts.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  const price = find(facts, 'price', 'level', 'index_value')
  const change = find(facts, 'change_pct', 'change_percent', 'change')
  if (
    caption?.trim().toUpperCase() === 'NEPSE' &&
    price &&
    change &&
    ['index', 'points'].includes(cellUnit(price.value))
  ) {
    return <IndexFacts facts={facts} caption={caption} lang={lang} />
  }
  return (
    <figure data-role="chart" data-component="facts" className="agent-facts m-0">
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      <dl>
        {facts.map((fact) => (
          <FactRow key={fact.id} fact={fact} lang={lang} />
        ))}
      </dl>
    </figure>
  )
}
